"use strict";
/*
 * IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: IBM Cognos Analytics
 *
 * (C) Copyright IBM Corp. 2020, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tile = exports.SelectableTile = exports.ExpandableTile = exports.ClickableTile = void 0;
const tslib_1 = require("tslib");
const prop_types_1 = tslib_1.__importDefault(require("prop-types"));
const react_1 = tslib_1.__importDefault(require("react"));
const carbon_components_react_1 = require("carbon-components-react");
const utils_1 = require("../utils");
const StringResource_1 = require("../nls/StringResource");
const CLICKABLETILE_COMPONENT_NAME = 'ClickableTile';
const CLICKABLETILE_DISPLAY_NAME = (0, utils_1.getDisplayName)(CLICKABLETILE_COMPONENT_NAME);
const CLICKABLETILE_DEFAULT_TID = (0, utils_1.getDefaultTid)(CLICKABLETILE_COMPONENT_NAME);
exports.ClickableTile = react_1.default.forwardRef((props, ref) => {
    const { 
    // remove - remapped and local props
    'data-tid': dataTid, handleClick, handleKeyDown } = props, 
    // pass all other props to Carbon
    otherCarbonProps = tslib_1.__rest(props, ['data-tid', "handleClick", "handleKeyDown"]);
    const { onSelect } = props;
    const handleOnClick = (e) => {
        handleClick === null || handleClick === void 0 ? void 0 : handleClick(e);
        onSelect === null || onSelect === void 0 ? void 0 : onSelect();
    };
    const handleOnKeyDown = (e) => {
        handleKeyDown === null || handleKeyDown === void 0 ? void 0 : handleKeyDown(e);
        if (e.key === 'SPACE' || e.key === 'ENTER') {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect();
        }
    };
    const mappedProps = {
        'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : CLICKABLETILE_DEFAULT_TID,
    };
    if (handleClick || onSelect) {
        mappedProps.handleClick = handleOnClick;
    }
    if (handleKeyDown || onSelect) {
        mappedProps.handleKeyDown = handleOnKeyDown;
    }
    if (ref) {
        mappedProps.ref = ref;
    }
    return react_1.default.createElement(carbon_components_react_1.ClickableTile, Object.assign({}, otherCarbonProps, mappedProps));
});
exports.ClickableTile.propTypes = {
    'data-tid': prop_types_1.default.string,
    handleClick: prop_types_1.default.func,
    handleKeyDown: prop_types_1.default.func,
    onSelect: prop_types_1.default.func,
};
exports.ClickableTile.displayName = CLICKABLETILE_DISPLAY_NAME;
const EXPANDABLETILE_COMPONENT_NAME = 'ExpandableTile';
const EXPANDABLETILE_DISPLAY_NAME = (0, utils_1.getDisplayName)(EXPANDABLETILE_COMPONENT_NAME);
const EXPANDABLETILE_DEFAULT_TID = (0, utils_1.getDefaultTid)(EXPANDABLETILE_COMPONENT_NAME);
const EXPANDABLETILE_TILE_COLLAPSED_ICON_TEXT = StringResource_1.stringResource.get('expandabletile_tileCollapsedIconText');
const EXPANDABLETILE_TILE_EXPANDED_ICON_TEXT = StringResource_1.stringResource.get('expandabletile_tileExpandedIconText');
exports.ExpandableTile = react_1.default.forwardRef((props, ref) => {
    const { 
    // remove - remapped and local props
    'data-tid': dataTid, tileCollapsedIconText, tileExpandedIconText } = props, 
    // pass all other props to Carbon
    otherCarbonProps = tslib_1.__rest(props, ['data-tid', "tileCollapsedIconText", "tileExpandedIconText"]);
    const mappedProps = {
        'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : EXPANDABLETILE_DEFAULT_TID,
        tileCollapsedIconText: tileCollapsedIconText !== null && tileCollapsedIconText !== void 0 ? tileCollapsedIconText : EXPANDABLETILE_TILE_COLLAPSED_ICON_TEXT,
        tileExpandedIconText: tileExpandedIconText !== null && tileExpandedIconText !== void 0 ? tileExpandedIconText : EXPANDABLETILE_TILE_EXPANDED_ICON_TEXT,
    };
    if (ref) {
        mappedProps.ref = ref;
    }
    return react_1.default.createElement(carbon_components_react_1.ExpandableTile, Object.assign({}, otherCarbonProps, mappedProps));
});
exports.ExpandableTile.propTypes = {
    'data-tid': prop_types_1.default.string,
    tileCollapsedIconText: prop_types_1.default.string,
    tileExpandedIconText: prop_types_1.default.string,
};
exports.ExpandableTile.displayName = EXPANDABLETILE_DISPLAY_NAME;
const SELECTABLETILE_COMPONENT_NAME = 'SelectableTile';
const SELECTABLETILE_DISPLAY_NAME = (0, utils_1.getDisplayName)(SELECTABLETILE_COMPONENT_NAME);
const SELECTABLETILE_DEFAULT_TID = (0, utils_1.getDefaultTid)(SELECTABLETILE_COMPONENT_NAME);
exports.SelectableTile = react_1.default.forwardRef((props, ref) => {
    const { 
    // remove - remapped and local props
    'data-tid': dataTid, handleClick, handleKeyDown } = props, 
    // pass all other props to Carbon
    otherCarbonProps = tslib_1.__rest(props, ['data-tid', "handleClick", "handleKeyDown"]);
    const { onSelect } = props;
    const handleOnClick = (e) => {
        handleClick === null || handleClick === void 0 ? void 0 : handleClick(e);
        onSelect === null || onSelect === void 0 ? void 0 : onSelect();
    };
    const handleOnKeyDown = (e) => {
        handleKeyDown === null || handleKeyDown === void 0 ? void 0 : handleKeyDown(e);
        if (e.key === 'SPACE' || e.key === 'ENTER') {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect();
        }
    };
    const mappedProps = {
        'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : SELECTABLETILE_DEFAULT_TID,
    };
    if (handleClick || onSelect) {
        mappedProps.handleClick = handleOnClick;
    }
    if (handleKeyDown || onSelect) {
        mappedProps.handleKeyDown = handleOnKeyDown;
    }
    if (ref) {
        mappedProps.ref = ref;
    }
    return react_1.default.createElement(carbon_components_react_1.SelectableTile, Object.assign({}, otherCarbonProps, mappedProps));
});
exports.SelectableTile.propTypes = {
    'data-tid': prop_types_1.default.string,
    handleClick: prop_types_1.default.func,
    handleKeyDown: prop_types_1.default.func,
    onSelect: prop_types_1.default.func,
};
exports.SelectableTile.displayName = SELECTABLETILE_DISPLAY_NAME;
const TILE_COMPONENT_NAME = 'Tile';
const TILE_DISPLAY_NAME = (0, utils_1.getDisplayName)(TILE_COMPONENT_NAME);
const TILE_DEFAULT_TID = (0, utils_1.getDefaultTid)(TILE_COMPONENT_NAME);
exports.Tile = react_1.default.forwardRef((props, ref) => {
    const { 
    // remove - remapped and local props
    'data-tid': dataTid } = props, 
    // pass all other props to Carbon
    otherCarbonProps = tslib_1.__rest(props, ['data-tid']);
    const mappedProps = {
        'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : TILE_DEFAULT_TID,
    };
    if (ref) {
        mappedProps.ref = ref;
    }
    return react_1.default.createElement(carbon_components_react_1.Tile, Object.assign({}, otherCarbonProps, mappedProps));
});
exports.Tile.propTypes = {
    'data-tid': prop_types_1.default.string,
};
exports.Tile.displayName = TILE_DISPLAY_NAME;
